/**
 * useOrgMembers hook
 *
 * Fetches organization members and handles role changes and deactivation.
 */

"use client";

import { useState, useCallback } from "react";
import { useApiQuery } from "@/hooks/useApiQuery";
import type { OrgRole } from "@/lib/organizations/types";

/** Shape of a member returned from the API */
export interface OrgMemberRow {
  id: string;
  user_id: string;
  role: OrgRole;
  status: "pending" | "active" | "deactivated";
  invited_at: string | null;
  joined_at: string | null;
  user: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
  };
}

export function useOrgMembers() {
  const {
    data: members,
    isLoading,
    error: fetchError,
    refresh,
  } = useApiQuery<OrgMemberRow[]>("/api/tenant/users", {
    errorMessage: "Benutzer konnten nicht geladen werden",
  });

  const [mutationError, setMutationError] = useState<string | null>(null);

  const updateMember = useCallback(
    async (
      memberId: string,
      data: { role?: OrgRole; status?: "deactivated" }
    ): Promise<boolean> => {
      setMutationError(null);

      try {
        const res = await fetch(`/api/tenant/users/${memberId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });

        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || "Änderung konnte nicht gespeichert werden");
        }

        // Refresh the list after update
        refresh();
        return true;
      } catch (err) {
        setMutationError(
          err instanceof Error ? err.message : "Änderung konnte nicht gespeichert werden"
        );
        return false;
      }
    },
    [refresh]
  );

  return {
    members: members ?? [],
    isLoading,
    error: mutationError || fetchError,
    fetchMembers: refresh,
    updateMember,
  };
}
